import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAddNoticiaMutation, useGetNoticiaQuery } from '../noticiaApiSlice';
import { useAddNoticiaAnexosMutation, useAddNoticiaCapaMutation, useGetNoticiaAnexosQuery, useRemoveNoticiaAnexoExcluidoMutation } from '../noticiaAnexoApiSlice';
import { enqueueSnackbar } from 'notistack';
import { setPublicacao, resetarEstado } from '../publicacaoSlice';
import { NoticiaDTO } from '../../../../../typesSite/noticia/Noticia';
import { NoticiaAnexo } from '../../../../../typesSite/noticia/NoticiaAnexo';
import convertImageToWeb from '../../../../../utils/convertImageToWeb';
import errorFormat from '../../../../../utils/errorFormat';
import { formatDateToISO } from '../../../../../utils/formatDate';
import { isValidDate } from '../../../../../utils/validDate';
import { useNoticiaStatusNotifier } from './useNoticiaStatusNotifier';
import { useNavigate } from 'react-router-dom';
import configSiteUrls from '../../../configs/configSiteUrls';

export function useNoticiaForm(id: string, publicacao: any) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [imagemCapa, setImagemCapa] = useState<File | null>(null);
  const [imagemUrl, setImagemUrl] = useState<string | null>(null);
  const [noticiaAnexoParaSalvar, setNoticiaAnexoParaSalvar] = useState<File[]>([]);
  const [noticiaAnexoSalvo, setNoticiaAnexoSalvo] = useState<NoticiaAnexo[] | null>(null);
  const [loading, setLoading] = useState(false);

  const { data, error } = useGetNoticiaQuery({ id: id || '', isPublicLink: false }, { skip: !id });
  const { data: dataAnexo } = useGetNoticiaAnexosQuery({ idNoticia: id });

  const [createNoticia, status] = useAddNoticiaMutation();
  const [createCapa, statusCreateCapa] = useAddNoticiaCapaMutation();
  const [createAnexos, statusCreateAnexos] = useAddNoticiaAnexosMutation();
  const [removeAnexosExcluidos, statusRemoveExcluido] = useRemoveNoticiaAnexoExcluidoMutation();
  useNoticiaStatusNotifier(status, 'Notícia gravada com sucesso', 'Erro ao gravar notícia');
  useNoticiaStatusNotifier(statusCreateCapa, 'Capa da notícia gravada com sucesso', 'Erro ao gravar a capa da notícia');
  useNoticiaStatusNotifier(statusCreateAnexos, 'Anexos da notícia gravada com sucesso', 'Erro ao gravar os anexos da notícia');
  useNoticiaStatusNotifier(statusRemoveExcluido, 'Anexos da notícia foram excluídos com sucesso', 'Erro ao excluir os anexos da notícia');

  useEffect(() => {
    if (data) dispatch(setPublicacao(data));
  }, [data]);
  useEffect(() => {
    if (dataAnexo) setNoticiaAnexoSalvo(dataAnexo);
  }, [dataAnexo]);

  const handleFileCapaChange = async (file: File) => {
    const convertedFile = await convertImageToWeb(file);
    setImagemCapa(convertedFile);
    setImagemUrl(URL.createObjectURL(convertedFile));
  };

  const handleFileAnexoChange = async (files: File[]) => {
    const convertedFiles = await Promise.all(files.map(file => convertImageToWeb(file)));
    setNoticiaAnexoParaSalvar(prev => [...prev, ...convertedFiles]);
  };

  const handleFileDeleteCapa = () => {
    setImagemCapa(null);
    setImagemUrl(null);
  };
  const handleOnDeleteParaSalvar = (index: number) => {
    setNoticiaAnexoParaSalvar(prev => prev.filter((_, i) => i !== index));
  };

  function handleOnDeleteSalvo(fileId: string) {
    setNoticiaAnexoSalvo(prev => {
      if (prev) {
        return prev.map(arq => (arq.id === fileId ? { ...arq, excluido: true } : arq));
      }
      return null; // ou um array vazio se preferir
    });
  }

  const handleExcluirTodosAnexos = () => {
    setNoticiaAnexoParaSalvar([]);
    setNoticiaAnexoSalvo(null);
  };

  const handleGravar = async () => {
    if (!publicacao.dataHoraLiberacao || !isValidDate(publicacao.dataHoraLiberacao)) {
      enqueueSnackbar('Data de Liberação inválida', { variant: 'error' });
      return;
    }

    if (!publicacao.titulo || publicacao.titulo.trim() === '') {
      enqueueSnackbar('Por favor, preencha o título!', { variant: 'error' });
      return;
    }

    if (!publicacao.corpo || publicacao.corpo.trim() === '') {
      enqueueSnackbar('Por favor, preencha o corpo!', { variant: 'error' });
      return;
    }

    const noticiaDTO: NoticiaDTO = {
      id: publicacao.id,
      titulo: publicacao.titulo,
      subtitulo: publicacao.subtitulo,
      resumo: publicacao.resumo,
      corpo: publicacao.corpo,
      imagem: publicacao.imagem,
      tipoID: '',
      dataHoraLiberacao: formatDateToISO(publicacao.dataHoraLiberacao),
      capa: publicacao.capa
    };

    setLoading(true);
    const result: any = await createNoticia(noticiaDTO);
    const idNoticia = result?.data?.id;

    if (idNoticia) {
      if (imagemCapa) {
        const formDataCapa = new FormData();
        formDataCapa.append('imagem_capa', imagemCapa);
        formDataCapa.append('noticiaID', idNoticia);
        createCapa(formDataCapa);
      }

      if (noticiaAnexoParaSalvar.length) {
        const formData = new FormData();
        noticiaAnexoParaSalvar.forEach(file => formData.append('imagens', file));
        formData.append('noticiaID', idNoticia);
        createAnexos(formData);
      }

      if (noticiaAnexoSalvo && noticiaAnexoSalvo?.length) {
        let noticiaAnexoExcluido: { idAnexo: string }[];

        noticiaAnexoExcluido = noticiaAnexoSalvo.filter(it => it.excluido === true).map(it => ({ idAnexo: it.id }));

        if (noticiaAnexoExcluido.length) {
          const formData = new FormData();
          formData.append('noticiaID', idNoticia);
          formData.append('anexosExcluidos', JSON.stringify(noticiaAnexoExcluido));
          removeAnexosExcluidos(formData);
        }
      }

      dispatch(resetarEstado());
      handleFileDeleteCapa();
      handleExcluirTodosAnexos();

      navigate(configSiteUrls.SITE_NOTICIA_LIST);
    }
    setLoading(false);
  };

  return {
    imagemCapa,
    imagemUrl,
    noticiaAnexoParaSalvar,
    noticiaAnexoSalvo,
    loading,
    handleFileCapaChange,
    handleFileAnexoChange, //para salvar
    handleFileDeleteCapa,
    handleOnDeleteParaSalvar,
    handleOnDeleteSalvo,
    handleExcluirTodosAnexos,
    handleGravar
  };
}
